/**
 * useAllProgramTypeStatuses Hook
 * Fetches all program type statuses without workflow filtering
 * Used for workflow creation and general status lists
 */

import { useState, useEffect } from 'react';
import { graphqlClient } from '@/services/core/graphqlClient';
import { GET_PROGRAM_TYPE_STATUSES } from '@/services/resources/workflowGquery';
import { useAuth } from '@/contexts/AuthContext';

export interface ProgramTypeStatus {
  program_status: string;
  program_status_id?: number;
  program_status_mapper_id: string;
  program_type: string;
  program_type_id?: number;
}

export interface UseAllProgramTypeStatusesResult {
  programTypeStatuses: ProgramTypeStatus[];
  loading: boolean;
  error: string | null;
}

export const useAllProgramTypeStatuses = (): UseAllProgramTypeStatusesResult => {
  const { hasuraToken } = useAuth();
  const [statuses, setStatuses] = useState<ProgramTypeStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAllStatuses = async () => {
      if (!hasuraToken) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await graphqlClient.query(
          GET_PROGRAM_TYPE_STATUSES,
          {},
          hasuraToken
        );

        if (response.success && response.data?.pep_vw_program_type_status) {
          setStatuses(response.data.pep_vw_program_type_status);
        } else {
          const errorMessage = response.errors?.join(', ') || 'Failed to fetch program type statuses';
          setError(errorMessage);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch program type statuses');
        console.error('Error fetching all program type statuses:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllStatuses();
  }, [hasuraToken]);

  return {
    programTypeStatuses: statuses,
    loading,
    error
  };
};
